import { navigate, type Screen } from '@/app'
import {
  browseCatalog,
  type ContentCard,
  getCollection,
  getCollections,
  getGenres,
  getPopular,
  getUpcoming,
  searchAll,
  type SearchPerson,
} from '@/lib/catalog'
import { formatPremiere } from '@/lib/dates'
import { getWatchlist } from '@/lib/watchlist'
import { clear, el } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { openWebDetail, openWebUpcoming } from '@/browser/detailModal'
import {
  webButton,
  webCard,
  webChip,
  webEmpty,
  webGrid,
  webPage,
  webPersonCard,
  webRow,
  webScreen,
  webTitle,
} from '@/browser/ui'

/**
 * Kataloq ekranları — brauzer variantı: Kəşf · Axtarış · Siyahı · Tezliklə.
 *
 * ⚠ FİLTR SERVER TƏRƏFDƏDİR (`lib/catalog.ts` qeydi) — burada da eynidir: bütün
 *   kataloqu çəkib brauzerdə süzmək TV brauzerinin yaddaşını doldurur.
 */

/**
 * Kart açılışı — FİLM MODALI (2026-08-13 sahib tələbi). Yaş qapısı modalın
 * içindədir, yəni 18+ kartda pəncərə açılmır (`browser/detailModal.ts`).
 */
function open(card: ContentCard): void {
  openWebDetail(card)
}

function cardsOf(items: ContentCard[]): HTMLElement[] {
  return items.map((card) => webCard(card, open))
}

// ── Siyahı (kolleksiya · ən çox baxılanlar) ────────────────────────────────

/**
 * ⚠ `genre` 2026-08-26-da əlavə olundu — uşaq ana ekranının «Nə baxaq?» kartları
 *   janra keçir (cihaz variantındakı eyni dəyişiklik). Mövcud iki növ dəyişmir.
 * ⚠ `watchlist` 2026-08-30-da əlavə olundu (sahib tələbi «sonra izlə modulu») —
 *   Profildəki bənd bu ekranı açır; cihaz variantındakı `screens/list.ts` ilə eyni
 *   qərar: ayrıca ekran faylı yazılmır, boş/xəta vəziyyətləri buradan gəlir.
 */
export function createWebListScreen(options: {
  kind: 'collection' | 'popular' | 'genre' | 'watchlist'
  slug?: string
  title?: string
}): Screen {
  const page = webPage()
  const heading = webTitle(options.title || listHeading(options.kind))
  page.appendChild(heading)

  const holder = el('div')
  holder.appendChild(webEmpty(T('Yüklənir…')))
  page.appendChild(holder)

  let alive = true
  /*
    ⚠ «Sonra İzlə» HƏR AÇILIŞDA UCDAN ÇƏKİLİR (cihaz `screens/list.ts`-dəki eyni
      səbəb): siyahı telefondan/saytdan da dəyişir. Girişsiz/profilsiz halda
      `getWatchlist()` şəbəkəyə çıxmır və boş massiv verir — aşağıdakı «Bu bölmə
      hələ boşdur» mesajı görünür.
  */
  const request =
    options.kind === 'popular'
      ? getPopular().then((items) => (items ? { title: T('Ən çox baxılanlar'), items } : null))
      : options.kind === 'watchlist'
        ? getWatchlist().then((items) => ({ title: T('Sonra İzlə'), items: items }))
        : options.kind === 'genre'
          ? browseCatalog(null, options.slug || '', 1, 48).then((found) =>
              found ? { title: options.title || '', items: found.items } : null,
            )
          : getCollection(options.slug || '')

  void request.then((result) => {
    if (!alive) return
    clear(holder)
    if (!result) {
      holder.appendChild(webEmpty(T('Siyahı yüklənmədi. Bir azdan yenidən yoxlayın.')))
      return
    }
    if (!result.items.length) {
      holder.appendChild(webEmpty(T('Bu bölmə hələ boşdur.')))
      return
    }
    holder.appendChild(webGrid(cardsOf(result.items)))
  })

  // 2026-10-23: «Geri» üçün saxlanılır — izah `browser/ui.ts` → `webScreen` (keep-alive).
  return webScreen(page, () => {
    alive = false
  }, {})
}

/** Siyahı ekranının sabit başlığı — çağırış yeri ad verməyəndə. */
function listHeading(kind: 'collection' | 'popular' | 'genre' | 'watchlist'): string {
  if (kind === 'popular') return T('Ən çox baxılanlar')
  if (kind === 'watchlist') return T('Sonra İzlə')
  return T('Siyahı')
}

// ── Tezliklə ───────────────────────────────────────────────────────────────

export function createWebUpcomingScreen(): Screen {
  const page = webPage()
  page.appendChild(webTitle(T('Tezliklə')))

  const holder = el('div')
  holder.appendChild(webEmpty(T('Yüklənir…')))
  page.appendChild(holder)

  let alive = true
  void getUpcoming().then((items) => {
    if (!alive) return
    clear(holder)
    if (!items) {
      holder.appendChild(webEmpty(T('Siyahı yüklənmədi. Bir azdan yenidən yoxlayın.')))
      return
    }
    if (!items.length) {
      holder.appendChild(webEmpty(T('Hazırda planlaşdırılmış premyera yoxdur.')))
      return
    }
    // ⚠ Premyera tarixi kartın meta sətrini ƏVƏZ EDİR — «Tezliklə»də il/janr
    //   deyil, MƏHZ tarix gözlənilir (523/528 müqaviləsi, üç səthdə eyni).
    /*
      ⚠⚠ «TEZLİKLƏ» KARTI YÜNGÜL MODAL AÇIR (523/528 müqaviləsi, üç TV səthində
         eyni): treyler + məlumat, «İzlə» və oxşarlar yoxdur.
      ⚠ ƏVVƏL BURADA YAŞ QAPISI ÜMUMİYYƏTLƏ YOX İDİ — kart birbaşa `detail`
        marşrutuna gedirdi (adi kartlardan fərqli olaraq `openWithAgeGate`-dən
        keçmirdi). İndi qapı `openWebUpcoming`-in içindədir, yəni 18+ premyera
        da yoxlanılır.
    */
    holder.appendChild(
      webGrid(
        items.map((item) =>
          webCard(item, () => openWebUpcoming(item), {
            meta: formatPremiere(item) || T('tarix açıqlanmayıb'),
          }),
        ),
      ),
    )
  })

  // 2026-10-23: «Geri» üçün saxlanılır — izah `browser/ui.ts` → `webScreen` (keep-alive).
  return webScreen(page, () => {
    alive = false
  }, {})
}

// ── Kəşf ───────────────────────────────────────────────────────────────────

/** Bir «daha çox» addımı. Səhifə 24-lükdür — TV brauzerində 24 poster rahat çəkilir. */
const PAGE_SIZE = 24

export function createWebBrowseScreen(): Screen {
  const page = webPage()
  page.appendChild(webTitle(T('Kataloq')))

  const genreBar = el('div')
  genreBar.style.marginTop = '6px'
  page.appendChild(genreBar)

  const holder = el('div')
  holder.style.marginTop = '18px'
  page.appendChild(holder)

  const footer = el('div')
  footer.style.textAlign = 'center'
  footer.style.marginTop = '10px'
  page.appendChild(footer)

  let alive = true
  let selectedGenre: string | null = null
  let loadedPages = 0
  let total = 0
  let items: ContentCard[] = []
  let grid: HTMLElement | null = null
  let renderedCount = 0
  /** Yolda olan səhifə sorğusu — zond iki dəfə işə düşsə ikinci gediş olmasın. */
  let loading = false
  let generation = 0
  /** Sürüşmə zondunun müşahidəçisi (dəstəklənməyən mühərrikdə `null` qalır). */
  let watcher: IntersectionObserver | null = null

  function stopWatch(): void {
    if (!watcher) return
    watcher.disconnect()
    watcher = null
  }

  function paintFilters(genres: Array<{ slug: string; name: string; count: number }>): void {
    clear(genreBar)
    genreBar.appendChild(
      webChip(T('Bütün janrlar'), selectedGenre === null, () => {
        selectedGenre = null
        paintFilters(genres)
        reload()
      }),
    )
    for (let i = 0; i < genres.length; i++) {
      const genre = genres[i]
      if (!genre) continue
      genreBar.appendChild(
        webChip(genre.name, genre.slug === selectedGenre, () => {
          selectedGenre = genre.slug
          paintFilters(genres)
          reload()
        }),
      )
    }
  }

  function paintItems(): void {
    stopWatch()
    clear(footer)
    if (!items.length) {
      clear(holder)
      holder.appendChild(webEmpty(T('Bu filtrdə nəticə yoxdur.')))
      return
    }
    // 2026-10-05: DOM zondu ikinci səhifədə ilk kartın silindiyini göstərdi.
    // Şəbəkəni bir dəfə qururuq; yeni səhifə mövcud şəkilləri yenidən yükləmir.
    if (!grid) {
      clear(holder)
      grid = webGrid([])
      holder.appendChild(grid)
    }
    for (; renderedCount < items.length; renderedCount++) {
      const card = items[renderedCount]
      if (card) grid.appendChild(webCard(card, open))
    }
    if (items.length >= total) return

    /*
      ⚠ SONSUZ SÜRÜŞMƏ + AÇIQ DÜYMƏ (2026-08-16 — TestingClaude veb TV 38).
        ÖLÇÜLDÜ: səhifənin dibinə sürüşüb 2.5 s gözlədikdə kart sayı 24-də QALIRDI;
        yeni kart YALNIZ «Daha çox göstər» düyməsi ilə gəlirdi.
        ƏVVƏLKİ QƏRARIN SƏBƏBİ (bu sətirdə yazılmışdı) `scroll` DİNLƏYİCİSİNƏ
        aid idi — o, TV brauzerində hər kadrda işə düşür və sürüşməni tutur.
        `IntersectionObserver` isə fərqli maşındır: geri çağırış yalnız zond
        görünəndə (kadrda bir dəfə) işləyir, mövqe hesabı brauzerin öz
        kompozisiya qatındadır, bizim kod nə `scrollTop` oxuyur, nə də layout
        məcbur edir. Yəni köhnə etiraz bu üsula aid deyil.
      ⚠ DƏSTƏK: `IntersectionObserver` Chrome 51+-dır, bizim döşəmə isə chrome53
        (`vite.config.ts`) — yəni dəstəklənən bütün TV-lərdə var. Olmayan
        mühərrikdə (köhnə webOS) yalnız düymə qalır: davranış bugünkü ilə eynidir.
      ⚠ DÜYMƏ SİLİNMİR: pultla gəzən istifadəçi üçün fokus dayanacağı lazımdır —
        sürüşmə jesti olmayan pultda tək çıxış yolu odur.
    */
    const sentinel = el('div')
    sentinel.style.height = '1px'
    footer.appendChild(sentinel)
    footer.appendChild(webButton(T('Daha çox göstər'), () => loadMore(), true))

    if (typeof IntersectionObserver === 'undefined') return
    watcher = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i]
          if (entry && entry.isIntersecting) {
            loadMore()
            return
          }
        }
      },
      // Dib görünməmişdən ~bir ekran əvvəl növbəti səhifə istənir.
      { rootMargin: '600px 0px' },
    )
    watcher.observe(sentinel)
  }

  function reload(): void {
    generation += 1
    loading = false
    loadedPages = 0
    items = []
    grid = null
    renderedCount = 0
    stopWatch()
    clear(holder)
    clear(footer)
    holder.appendChild(webEmpty(T('Yüklənir…')))
    loadMore()
  }

  function loadMore(): void {
    // ⚠ QAPI: zond görünəndə və düymə basılanda eyni funksiya çağırılır — qapısız
    //   olsa eyni səhifə iki dəfə çəkilər və kartlar TƏKRARLANARDI.
    if (!alive || loading) return
    loading = true
    const asked = generation
    const wanted = loadedPages + 1
    void browseCatalog(null, selectedGenre, wanted, PAGE_SIZE).then((result) => {
      if (!alive || asked !== generation) return
      loading = false
      if (!result) {
        stopWatch()
        clear(footer)
        if (!items.length) {
          clear(holder)
          holder.appendChild(webEmpty(T('Kataloq yüklənmədi. Bir azdan yenidən yoxlayın.')))
        }
        footer.appendChild(webButton(T('Yenidən cəhd et'), () => loadMore(), true))
        return
      }
      loadedPages = wanted
      total = result.items.length ? result.total : items.length
      const seen: { [id: string]: boolean } = {}
      for (let i = 0; i < items.length; i++) seen[(items[i] as ContentCard).id] = true
      for (let i = 0; i < result.items.length; i++) {
        const card = result.items[i]
        if (!card || seen[card.id]) continue
        seen[card.id] = true
        items.push(card)
      }
      paintItems()
    })
  }

  void getGenres().then((genres) => {
    if (!alive) return
    paintFilters(genres)
  })
  paintFilters([])
  reload()

  // 2026-10-23: «Geri» üçün saxlanılır (janr · yüklənmiş səhifələr · sürüşmə qalır) — izah `browser/ui.ts` → `webScreen`.
  return webScreen(page, () => {
    alive = false
    // Ekran söküləndə müşahidəçi də bağlanır — əks halda o, artıq sənəddə
    // olmayan zondu tutmağa davam edərdi (sızma).
    stopWatch()
  }, {})
}

// ── Axtarış ────────────────────────────────────────────────────────────────

/** Yazı dayandıqdan sonra sorğuya qədər gözləmə. */
const DEBOUNCE_MS = 350

export function createWebSearchScreen(): Screen {
  const page = webPage()
  page.appendChild(webTitle(T('Axtarış')))

  /**
   * ⚠ HƏQİQİ `<input>` — CİHAZ EKRANINDAN ƏSAS FƏRQ. Orada ekran klaviaturası
   *   pultla gəzilən 40 düymədir; brauzerdə isə istifadəçinin öz klaviaturası
   *   (və TV brauzerinin öz ekran klaviaturası) var və onu əvəz etmək yalnız
   *   maneədir.
   */
  const form = el('div', 'web-search__form')
  const input = document.createElement('input')
  input.type = 'search'
  input.className = 'web-input web-focusable'
  input.placeholder = T('Film, serial, aktyor…')
  input.autocomplete = 'off'
  form.appendChild(input)
  form.appendChild(webButton(T('Axtar'), () => run(input.value)))
  page.appendChild(form)

  const holder = el('div')
  holder.style.marginTop = '18px'
  holder.appendChild(webEmpty(T('Ən azı iki hərf yaz.')))
  page.appendChild(holder)

  let alive = true
  let timer = 0
  /** Yolda olan sorğunun nömrəsi — gec gələn cavab təzəsini üstələməsin. */
  let ticket = 0

  function run(query: string): void {
    const mine = ++ticket
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      clear(holder)
      holder.appendChild(webEmpty(T('Ən azı iki hərf yaz.')))
      return
    }
    clear(holder)
    holder.appendChild(webEmpty(T('Axtarılır…')))
    void searchAll(trimmed).then((result) => {
      // ⚠ NÖMRƏ YOXLAMASI: «bat» sorğusu «batman»dan sonra qayıdıb nəticəni
      //   əvəz edə bilərdi — istifadəçi yazdığından başqa siyahı görərdi.
      if (!alive || mine !== ticket) return
      clear(holder)
      if (!result.contents.length && !result.persons.length) {
        holder.appendChild(webEmpty(T('Nəticə tapılmadı.')))
        return
      }
      /*
        ⚠ ŞƏXS SIRASI ƏN ÜSTDƏ (2026-08-16 defekti — TestingClaude veb TV 37).
          ÖLÇÜLDÜ: «sev» sorğusu bu ekranda 2 film + 0 ŞƏXS verirdi, eyni sorğu
          saytda 2 film + 4 şəxs qaytarır; «Aqşin» (aktyor adı) isə «Nəticə
          tapılmadı» ilə bitirdi. Səbəb: bu ekran `searchCatalog`-u çağırırdı, o isə
          server cavabındakı `persons` massivini ATIRDI (yalnız `contents` qaytarır).
          Cihaz (Samsung/LG) hədəfi artıq `searchAll` işlədirdi — yəni 2026-08-13
          düzəlişi brauzer hədəfinə gəlməmişdi.
        ⚠ SIRA (şəbəkə deyil) — Android TV və cihaz app-ındakı yerləşmə ilə eyni:
          şəxs sayı azdır, üfüqi sıra film şəbəkəsini aşağı itələməsin.
      */
      if (result.persons.length) {
        holder.appendChild(
          webRow(
            T('Şəxslər'),
            result.persons.map((person: SearchPerson) =>
              webPersonCard(person, () => navigate({ name: 'person', slug: person.slug })),
            ),
          ),
        )
      }
      if (result.contents.length) {
        if (result.persons.length) holder.appendChild(el('h2', 'web-row__title', T('Filmlər')))
        holder.appendChild(webGrid(cardsOf(result.contents)))
      }
    })
  }

  input.oninput = () => {
    // 2026-10-04: silinmiş sorğunun gecikmiş cavabı boş sahəni yenidən doldururdu.
    ticket += 1
    if (timer) window.clearTimeout(timer)
    const value = input.value
    timer = window.setTimeout(() => run(value), DEBOUNCE_MS)
  }
  input.onkeydown = (event: KeyboardEvent) => {
    if (event.keyCode === 13) {
      if (timer) window.clearTimeout(timer)
      run(input.value)
    }
  }

  // 2026-10-23: «Geri» üçün saxlanılır — izah `browser/ui.ts` → `webScreen` (keep-alive).
  return webScreen(page, () => {
    alive = false
    if (timer) window.clearTimeout(timer)
  }, {})
}

/** Website collection layout: filters above horizontal rows of film cards. */
export function createWebCollectionsScreen(): Screen {
  const page = webPage()
  page.appendChild(webTitle(T('Kolleksiyalar')))
  const filters = el('div', 'web-collections__filters')
  filters.setAttribute('role', 'tablist')
  const holder = el('div')
  page.appendChild(filters)
  page.appendChild(holder)
  let alive = true

  function load(): void {
    clear(filters)
    clear(holder)
    holder.appendChild(webEmpty(T('Yüklənir…')))
    void getCollections().then((collections) => {
      if (!alive) return
      clear(holder)
      if (collections === null) {
        holder.appendChild(webEmpty(T('Siyahı yüklənmədi. Bir azdan yenidən yoxlayın.')))
        holder.appendChild(webButton(T('Yenidən cəhd et'), load))
        return
      }
      if (!collections.length) {
        holder.appendChild(webEmpty(T('Bu bölmə hələ boşdur.')))
        return
      }
      let active: string | null = null
      const rows: Array<{ slug: string; node: HTMLElement }> = []
      const chips: Array<{ slug: string | null; node: HTMLElement }> = []
      function select(slug: string | null): void {
        active = slug
        chips.forEach((chip) => {
          const selected = chip.slug === active
          chip.node.classList.toggle('is-active', selected)
          chip.node.setAttribute('aria-selected', String(selected))
        })
        rows.forEach((row) => { row.node.hidden = active !== null && row.slug !== active })
      }
      function addChip(title: string, slug: string | null): void {
        const chip = webChip(title, slug === active, () => select(slug))
        chip.setAttribute('role', 'tab')
        chip.setAttribute('aria-selected', String(slug === active))
        filters.appendChild(chip)
        chips.push({ slug, node: chip })
      }
      addChip(T('Hamısı'), null)
      collections.forEach((collection) => {
        addChip(collection.title, collection.slug)
        const row = webRow(collection.title, cardsOf(collection.items), {
          label: T('Hamısı ›'),
          onClick: () => navigate({ name: 'list', kind: 'collection', slug: collection.slug, title: collection.title }),
        })
        rows.push({ slug: collection.slug, node: row })
        holder.appendChild(row)
      })
    })
  }
  load()
  // 2026-10-23: «Geri» üçün saxlanılır — izah `browser/ui.ts` → `webScreen` (keep-alive).
  return webScreen(page, () => { alive = false }, {})
}
